import { supabase } from "@/integrations/supabase/client";
import { Banner, BannerCreateData, BannerUpdateData } from "@/types/banner";

/**
 * Buscar todos os banners para administração
 */
export const fetchAllBanners = async (): Promise<Banner[]> => {
  try {
    console.log('🔍 [fetchAllBanners] Buscando todos os banners para admin...');
    const { data, error } = await supabase
      .from("banners")
      .select(`
        *,
        desktop_image_position,
        desktop_image_object_position,
        desktop_image_scale
      `)
      .order("page", { ascending: true })
      .order("display_order", { ascending: true });

    if (error) {
      // Se a tabela não existe ainda (erro 404), retorna array vazio
      if (error.message.includes('does not exist') || error.code === 'PGRST116') {
        console.warn("Tabela banners ainda não foi criada");
        return [];
      }
      throw error;
    }
    
    console.log('📋 [fetchAllBanners] Dados retornados:', data?.map(banner => ({
      id: banner.id,
      title: banner.title,
      desktop_image_position: banner.desktop_image_position,
      desktop_image_object_position: banner.desktop_image_object_position,
      desktop_image_scale: banner.desktop_image_scale
    })));
    
    return (data || []) as Banner[];
  } catch (error) {
    console.error("Error fetching banners:", error);
    // Se é um erro de rede ou de tabela não existir, retorna array vazio
    if (error instanceof Error && (
      error.message.includes('404') || 
      error.message.includes('does not exist') ||
      error.message.includes('PGRST116')
    )) {
      console.warn("Tabela banners ainda não foi criada, retornando array vazio");
      return [];
    }
    throw error;
  }
};

/**
 * Buscar banners ativos por página específica
 */
export const fetchBannersByPage = async (page: 'mentores' | 'jogos' | 'mentorias' | 'comunidades' | 'guias'): Promise<Banner[]> => {
  try {
    console.log(`🔍 [fetchBannersByPage] Buscando banners para página: ${page}`);
    
    const { data, error } = await supabase
      .from("banners")
      .select("*")
      .eq("page", page)
      .eq("is_active", true)
      .order("display_order", { ascending: true });

    if (error) throw error;
    
    console.log(`📊 [fetchBannersByPage] Banners encontrados:`, data?.length || 0);
    
    return (data || []) as Banner[];
  } catch (error) {
    console.error(`Error fetching banners for page ${page}:`, error);
    return [];
  }
};

/**
 * Criar novo banner
 */
export const createBanner = async (bannerData: BannerCreateData): Promise<Banner> => {
  try {
    const { data, error } = await supabase
      .from("banners")
      .insert([bannerData])
      .select()
      .single();

    if (error) throw error;
    return data as Banner;
  } catch (error) {
    console.error("Error creating banner:", error);
    throw error;
  }
};

/**
 * Atualizar banner existente
 */
export const updateBanner = async (id: string, bannerData: BannerUpdateData): Promise<Banner> => {
  try {
    const { data, error } = await supabase
      .from("banners")
      .update(bannerData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as Banner;
  } catch (error) {
    console.error("Error updating banner:", error);
    throw error;
  }
};

/**
 * Deletar banner
 */
export const deleteBanner = async (id: string): Promise<void> => {
  try {
    const { error } = await supabase
      .from("banners")
      .delete()
      .eq("id", id);

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting banner:", error);
    throw error;
  }
};

/**
 * Upload de imagem para banners
 */
export const uploadBannerImage = async (file: File, isMobile: boolean = false): Promise<string> => {
  try {
    const timestamp = Date.now();
    const prefix = isMobile ? 'mobile_' : 'desktop_';
    const fileName = `${prefix}banner_${timestamp}.${file.name.split('.').pop()}`;
    
    const { data, error } = await supabase.storage
      .from("banners")
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    const { data: publicData } = supabase.storage
      .from("banners")
      .getPublicUrl(data.path);

    return publicData.publicUrl;
  } catch (error) {
    console.error("Error uploading banner image:", error);
    throw error;
  }
}; 